// ---------------------------------------------------------------------------
// Client-side image downscaling
// ---------------------------------------------------------------------------
// Restaurant photos were stored and served exactly as they came off the
// owner's phone. A current phone camera produces a 3-4 MB, ~4000px JPEG, and
// the listings page renders it into a 72px thumbnail — so every visitor
// downloaded the whole original to display a postage stamp. With six or more
// rows on screen after the Option A redesign, that is the single largest thing
// slowing the site down on mobile.
//
// Shrinking here rather than in a Cloud Function means the big file never
// leaves the owner's phone: their upload is faster too, and there is no
// server-side processing to pay for.
// ---------------------------------------------------------------------------

// 1600px on the long edge covers the largest place a photo is ever displayed
// (the restaurant detail hero) at 2x for a retina screen. Beyond that is
// detail no visitor can see.
const MAX_DIMENSION = 1600;
const QUALITY = 0.82;

// Scale a source box down to fit within `max` on its longest edge, preserving
// aspect ratio. Never scales up. Pure, so it can be tested without a canvas.
export function fitWithin(width, height, max = MAX_DIMENSION) {
  const longest = Math.max(width, height);
  if (!Number.isFinite(longest) || longest <= 0) return { width, height };
  if (longest <= max) return { width, height };
  const scale = max / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

let webpSupport = null;
function supportsWebp() {
  if (webpSupport !== null) return webpSupport;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    webpSupport = false;
  }
  return webpSupport;
}

// createImageBitmap is the fast path and applies EXIF rotation for us — a
// phone photo taken in portrait is stored landscape with an orientation flag,
// and drawing it without honouring that flag silently rotates every listing
// photo 90 degrees. Safari only gained the option relatively recently, hence
// the <img> fallback, which applies orientation itself.
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* fall through to the <img> path */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not read that image.'));
      el.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function sourceSize(source) {
  return {
    width: source.width || source.naturalWidth || 0,
    height: source.height || source.naturalHeight || 0,
  };
}

function renameTo(name, extension) {
  return `${String(name || 'photo').replace(/\.[^.]+$/, '')}.${extension}`;
}

/**
 * Returns a smaller version of `file`, or the original when shrinking it would
 * not help. Never throws: a failure here should degrade to uploading what the
 * owner picked, not block their listing.
 */
export async function downscaleImage(file, options = {}) {
  const maxDimension = options.maxDimension ?? MAX_DIMENSION;
  const quality = options.quality ?? QUALITY;

  if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type)) return file;

  let source;
  try {
    source = await decode(file);
  } catch {
    return file;
  }

  try {
    const { width, height } = sourceSize(source);
    if (!width || !height) return file;

    const target = fitWithin(width, height, maxDimension);
    const type = supportsWebp() ? 'image/webp' : 'image/jpeg';
    // Nothing to gain: already small enough and already in a compact format.
    if (target.width === width && target.height === height && file.type === type) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, target.width, target.height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, type, quality));
    if (!blob) return file;

    // A small, already-optimised upload can come out larger after a re-encode
    // (PNG logos especially). Keep whichever is actually smaller.
    if (blob.size >= file.size) return file;

    return new File([blob], renameTo(file.name, type === 'image/webp' ? 'webp' : 'jpg'), {
      type,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    if (typeof source.close === 'function') source.close();
  }
}
